/**
 * Proactive Validation Hook
 *
 * Validates workflow in the background as user makes changes.
 * Uses debouncing to avoid excessive API calls.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { useValidationStore } from '@/stores/validationStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { validateWorkflowWithAzure } from '@/services/workflowValidationService';

/** Debounce delay in milliseconds (validate 2s after last change) */
const VALIDATION_DEBOUNCE_MS = 2000;

/** Minimum interval between validations to avoid rate limiting */
const MIN_VALIDATION_INTERVAL_MS = 5000;

interface UseProactiveValidationOptions {
  /** Workflow name for validation */
  workflowName: string;
  /** Whether to enable proactive validation (default: true) */
  enabled?: boolean;
}

interface UseProactiveValidationResult {
  /** Whether validation is currently running */
  isValidating: boolean;
  /** Per-node errors */
  nodeErrors: Record<string, import('@/services/workflowValidationService').ValidationError[]>;
  /** Global errors */
  globalErrors: import('@/services/workflowValidationService').ValidationError[];
  /** Total error count */
  errorCount: number;
  /** Whether validation has been run at least once */
  hasValidated: boolean;
  /** Manually trigger validation */
  triggerValidation: () => void;
  /** Clear all validation errors */
  clearErrors: () => void;
}

/**
 * Hook for proactive background validation of workflow changes.
 *
 * Automatically validates the workflow when:
 * - Workflow definition changes (debounced)
 * - Manual trigger via triggerValidation()
 *
 * Results are stored in validationStore and can be used to show
 * error badges on canvas nodes.
 */
export function useProactiveValidation({
  workflowName,
  enabled = true,
}: UseProactiveValidationOptions): UseProactiveValidationResult {
  const { getOttoToken } = useAuth();
  const { selectedProject, selectedApp } = useAppStore();
  const workflow = useWorkflowStore((state) => state.workflow);

  // Validation store state
  const isValidating = useValidationStore((state) => state.isValidating);
  const nodeErrors = useValidationStore((state) => state.nodeErrors);
  const globalErrors = useValidationStore((state) => state.globalErrors);
  const hasValidated = useValidationStore((state) => state.hasValidated);
  const setValidationErrors = useValidationStore((state) => state.setValidationErrors);
  const clearValidationErrors = useValidationStore((state) => state.clearValidationErrors);
  const setIsValidating = useValidationStore((state) => state.setIsValidating);
  const getTotalErrorCount = useValidationStore((state) => state.getTotalErrorCount);

  // Track last validation time to enforce minimum interval
  const lastValidationTimeRef = useRef<number>(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  // Track current workflow to discard stale in-flight validation results
  const currentWorkflowNameRef = useRef(workflowName);
  // Track last workflow content to avoid validating unchanged workflows
  const lastWorkflowJsonRef = useRef<string | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Clear validation errors when workflow changes
  useEffect(() => {
    currentWorkflowNameRef.current = workflowName;
    clearValidationErrors();
  }, [workflowName, clearValidationErrors]);

  // Core validation function
  const runValidation = useCallback(async (options?: { showToast?: boolean }) => {
    const showToast = options?.showToast ?? false;
    if (!enabled || !selectedProject || !selectedApp || !workflowName || !workflow) {
      return;
    }

    // Enforce minimum interval between validations
    const now = Date.now();
    const timeSinceLastValidation = now - lastValidationTimeRef.current;
    if (timeSinceLastValidation < MIN_VALIDATION_INTERVAL_MS && lastValidationTimeRef.current > 0) {
      return;
    }

    setIsValidating(true);
    lastValidationTimeRef.current = now;

    try {
      // Include the in-memory (draft) connections so the backend resolves
      // references against what the user is currently editing, not just
      // what's been committed to connections.json on disk.
      const draftConnections = useConnectionStore.getState().exportToConnectionsConfig();
      const hasConnections = Object.keys(draftConnections).length > 0;

      const result = await validateWorkflowWithAzure({
        projectName: selectedProject,
        appName: selectedApp.name,
        getToken: getOttoToken,
        workflowName,
        workflowDefinition: workflow,
        ...(hasConnections ? { connections: draftConnections } : {}),
      });

      if (isMountedRef.current && currentWorkflowNameRef.current === workflowName) {
        setValidationErrors(result.errors);

        if (showToast) {
          const { addNotification } = useNotificationStore.getState();
          if (result.isValid) {
            addNotification({
              type: 'success',
              title: 'Validation passed',
              message: `Workflow "${workflowName}" is valid and ready to run.`,
            });
          } else {
            const blockingCount = result.errors.filter((e) => e.severity !== 'Warning').length;
            const warningOnly = blockingCount === 0;
            addNotification({
              type: warningOnly ? 'warning' : 'error',
              title: warningOnly ? 'Validation completed with warnings' : 'Validation failed',
              message: `Found ${result.validationCount} issue${result.validationCount !== 1 ? 's' : ''} in workflow "${workflowName}".`,
              validationErrors: result.errors,
            });
          }
        }
      }
    } catch (error) {
      console.warn('[useProactiveValidation] Validation failed:', error);
      if (showToast && isMountedRef.current) {
        const { addNotification } = useNotificationStore.getState();
        addNotification({
          type: 'error',
          title: 'Validation failed',
          message: error instanceof Error ? error.message : 'Could not validate workflow.',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsValidating(false);
      }
    }
  }, [enabled, selectedProject, selectedApp, workflowName, workflow, setIsValidating, getOttoToken, setValidationErrors]);

  // Debounced validation trigger
  const scheduleValidation = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      runValidation();
    }, VALIDATION_DEBOUNCE_MS);
  }, [runValidation]);

  // Watch for workflow changes and trigger validation
  // Only validate when actual content changes, not just object reference
  useEffect(() => {
    if (!enabled || !workflow) {
      return;
    }

    // Serialize workflow to compare content (excluding notes which don't affect validation)
    const { notes: _notes, ...workflowWithoutNotes } = workflow;
    const currentJson = JSON.stringify(workflowWithoutNotes);

    // Skip validation if content hasn't changed
    if (currentJson === lastWorkflowJsonRef.current) {
      return;
    }
    lastWorkflowJsonRef.current = currentJson;

    // Schedule validation when workflow content actually changes
    scheduleValidation();

    // Cleanup timer on workflow change or unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [enabled, workflow, scheduleValidation]);

  // Manual trigger
  const triggerValidation = useCallback(() => {
    // Cancel any pending debounced validation
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    // Reset throttle so an explicit user-initiated validation always runs
    lastValidationTimeRef.current = 0;
    // Run immediately and surface result via toast
    runValidation({ showToast: true });
  }, [runValidation]);

  return {
    isValidating,
    nodeErrors,
    globalErrors,
    errorCount: getTotalErrorCount(),
    hasValidated,
    triggerValidation,
    clearErrors: clearValidationErrors,
  };
}

/** Stable empty array to avoid creating new references on each render */
const EMPTY_ERRORS: import('@/services/workflowValidationService').ValidationError[] = [];

/**
 * Hook to get validation errors for a specific node.
 * Use this in node components to check if they have errors.
 */
export function useNodeValidationErrors(nodeName: string) {
  const errors = useValidationStore((state) => state.nodeErrors[nodeName] ?? EMPTY_ERRORS);
  const hasErrors = errors.length > 0;

  return {
    errors,
    hasErrors,
    errorCount: errors.length,
  };
}
